import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '@/components/Sidebar';
import { AuthGuard } from '@/guards';
import { PageHeader } from '@/components/common/PageHeader';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { listDocs, createDoc, type DocListItem } from '@/api/docs';
import { trackCreateDoc } from '@/lib/analytics';
import { useChatContext } from '@/contexts/ChatContext';
import { useAuth } from '@/contexts/AuthContext';

export default function DocsPage() {
  const navigate = useNavigate();
  const { clearChat } = useChatContext();
  const [documents, setDocuments] = useState<DocListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const docsData = await listDocs();
        if (!alive) return;
        setDocuments(docsData);
      } catch {
        if (!alive) return;
        setError('Não foi possível carregar os dados.');
      } finally {
        if (alive) setIsLoading(false);
      }
    };

    void loadData();
    return () => {
      alive = false;
    };
  }, []);

  const handleCreateNew = async () => {
    setIsCreating(true);
    try {
      clearChat();
      const newDoc = await createDoc({ title: 'Rascunho' });
      trackCreateDoc();
      navigate(`/docs/${newDoc.id}`);
    } catch {
      setError('Não foi possível criar o documento.');
      setIsCreating(false);
    }
  };

  return (
    <AuthGuard>
      <div className="flex h-screen theme-bg overflow-hidden">
        <div className="flex-shrink-0">
          <Sidebar />
        </div>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <PageHeader
            title="Docs"
            subtitle="Gerencie seus documentos"
            action={
              <button
                type="button"
                onClick={handleCreateNew}
                disabled={isCreating}
                className="inline-flex items-center justify-center h-8 px-4 gap-2 rounded-lg text-xs font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 bg-[color:var(--nt-primary)] text-white fill-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover)]"
              >
                {isCreating ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Criando...</span>
                  </>
                ) : (
                  <>
                    <svg className="inline-flex size-4 fill-inherit" width="16" height="16" viewBox="0 0 16 16">
                      <path d="M8.663 3.33l-.001 4h4.001v1.333H8.662l.001 4H7.33v-4h-4V7.33h4v-4z" />
                    </svg>
                    <span>Novo Documento</span>
                  </>
                )}
              </button>
            }
          />

          <div className="p-8">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <ErrorState message={error} />
            ) : documents.length === 0 ? (
              <EmptyState
                message="Você ainda não possui documentos."
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {documents.map((doc) => (
                  <DocCard key={doc.id} document={doc} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </AuthGuard>
  );
}

function DocCard({ document }: { document: DocListItem }) {
  const { user } = useAuth();
  const [isAvatarHovered, setIsAvatarHovered] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();

    if (diffInMs < 0 || diffInMs < 30000) return 'quase agora';

    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'quase agora';
    if (diffInMinutes === 1) return 'há 1 minuto';
    if (diffInMinutes < 60) return `há ${diffInMinutes} minutos`;
    if (diffInHours === 1) return 'há 1 hora';
    if (diffInHours < 24) return `há ${diffInHours} horas`;
    if (diffInDays === 1) return 'ontem';
    if (diffInDays < 7) return `há ${diffInDays} dias`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  // Strip markdown formatting for preview
  const getPreview = (content?: string) => {
    if (!content) return '';
    return content
      .replace(/#{1,6}\s/g, '')
      .replace(/\*\*|__|~~|`/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/\n+/g, ' ')
      .trim();
  };

  // Get user initials
  const displayName = user?.name || user?.email || 'Usuário';
  const initials = displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

  const preview = getPreview(document.content);

  return (
    <Link
      to={`/docs/${document.id}`}
      className="group relative flex flex-col rounded-2xl border theme-border bg-white dark:bg-[#1a1a1d] hover:border-[color:var(--nt-primary)]/40 transition-all duration-300 hover:shadow-xl hover:shadow-[color:var(--nt-primary)]/5 hover:-translate-y-0.5"
    >
      {/* Content Preview */}
      <div className="p-4 pb-3 min-h-[60px]">
        {preview && (
          <p className="text-xs theme-text-muted line-clamp-3 leading-relaxed opacity-80">
            {preview}
          </p>
        )}
      </div>

      {/* Full Width Divider */}
      <div className="w-full h-px bg-[color:var(--card-border)]" />

      {/* Footer */}
      <div className="p-4 pt-3 flex flex-row items-center justify-between gap-2">
        {/* Title + Date */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold theme-text group-hover:text-[color:var(--nt-primary)] transition-colors truncate">
            {document.title || 'Sem título'}
          </h3>
          <p className="text-[11px] theme-text-muted opacity-60 mt-0.5">
            Atualizado {formatDate(document.updatedAt || document.createdAt)}
          </p>
        </div>

        {/* Avatar with Dropdown */}
        <div
          ref={avatarRef}
          className="relative flex-shrink-0"
          onMouseEnter={() => setIsAvatarHovered(true)}
          onMouseLeave={() => setIsAvatarHovered(false)}
          onClick={(e) => e.preventDefault()}
        >
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold border-2 bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)] cursor-pointer"
            title={displayName}
          >
            {initials}
          </div>

          {/* Avatar Dropdown */}
          <AnimatePresence>
            {isAvatarHovered && (
              <motion.div
                initial={{ opacity: 0, y: 5, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 5, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full mt-2 w-48 rounded-xl border theme-border theme-bg shadow-2xl z-[100] overflow-visible"
                onMouseEnter={() => setIsAvatarHovered(true)}
                onMouseLeave={() => setIsAvatarHovered(false)}
              >
                <div className="px-3 py-2 border-b theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_95%,transparent)]">
                  <div className="text-[10px] font-semibold theme-text-muted uppercase tracking-wider">
                    Autor
                  </div>
                </div>
                <div className="py-2">
                  <div className="flex items-center gap-2.5 px-3 py-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium theme-text truncate">
                        {displayName}
                        <span className="ml-1 text-[10px] theme-text-muted">(você)</span>
                      </div>
                      <div className="text-[10px] theme-text-muted">Proprietário</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Hover indicator */}
      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-[color:var(--accent-purple-600)] to-[color:var(--accent-pink-500)] opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
}
